import React from 'react';
import { ModelType } from './types';
import { useAdminTranslation } from '../../../modules/i18n/hooks/useTranslation';

interface ModelTabsProps {
  activeTab: ModelType;
  onTabChange: (tab: ModelType) => void;
}

interface TabConfig {
  type: ModelType;
  titleKey: string;
  iconColor: string;
  icon: React.ReactElement;
}

const ModelTabs: React.FC<ModelTabsProps> = ({ activeTab, onTabChange }) => {
  const { t } = useAdminTranslation();

  const tabs: TabConfig[] = [
    {
      type: 'embedding',
      titleKey: 'models.embedding.title',
      iconColor: 'text-blue-600 dark:text-blue-400',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 7v10c0 2.21 3.58 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.58 4 8 4s8-1.79 8-4M4 7c0-2.21 3.58-4 8-4s8 1.79 8 4"
          />
        </svg>
      ),
    },
    {
      type: 'ranking',
      titleKey: 'models.ranking.title',
      iconColor: 'text-green-600 dark:text-green-400',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      ),
    },
    {
      type: 'llm',
      titleKey: 'models.llm.title',
      iconColor: 'text-purple-600 dark:text-purple-400',
      icon: (
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
    },
  ];

  return (
    <div className='border-b border-gray-200 dark:border-border-dark'>
      <nav className='-mb-px flex space-x-8 overflow-x-auto' aria-label='Model types'>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.type;
          return (
            <button
              key={tab.type}
              onClick={() => onTabChange(tab.type)}
              className={`flex items-center whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${isActive
                  ? 'border-primary text-primary dark:text-dark-text'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-dark-text-secondary dark:hover:text-dark-text-secondary'
                }`}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className={`mr-2 ${isActive ? tab.iconColor : ''}`}>{tab.icon}</span>
              <span>{t(tab.titleKey)}</span>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default ModelTabs;